import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FlaskConical, Award, ArrowLeft } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';

const MostRigorous = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const data = await api.getMostRigorous(20);
      setArticles(data);
    } catch (error) {
      toast.error('Failed to load most rigorous articles');
    }
    setLoading(false);
  };

  const getEvidenceLevelColor = (level) => {
    switch (level) {
      case 'Level I': return 'bg-green-100 text-green-800';
      case 'Level II': return 'bg-blue-100 text-blue-800';
      case 'Level III': return 'bg-yellow-100 text-yellow-800';
      case 'Level IV': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center space-x-2">
              <FlaskConical className="h-8 w-8 text-green-600" />
              <span className="text-xl font-bold text-slate-900 dark:text-white">Most Rigorous Studies</span>
            </Link>
            <Link to="/explorer">
              <Button variant="ghost" data-testid="back-to-explorer-btn">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Explorer
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center mb-8">
          <Award className="h-10 w-10 text-green-600 mr-4" />
          <div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white" data-testid="page-title">Most Rigorous Studies</h1>
            <p className="text-slate-600 dark:text-slate-400">Top methodologically rigorous research (highest methodology scores)</p>
          </div>
        </div>

        <div className="space-y-4" data-testid="articles-list">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading most rigorous studies...</p>
            </div>
          ) : (
            articles.map((article, index) => (
              <Card key={article.id} className="hover:shadow-lg transition-shadow" data-testid={`article-card-${article.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <Link to={`/article/${article.id}`}>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white hover:text-green-600 mb-2" data-testid="article-title">
                          {article.title}
                        </h3>
                      </Link>
                      <div className="flex items-center space-x-4 text-sm text-slate-600 dark:text-slate-400 mb-3">
                        {article.journal && <span data-testid="article-journal">{article.journal}</span>}
                        {article.publication_year && <span data-testid="article-year">{article.publication_year}</span>}
                      </div>
                      <div className="flex items-center space-x-2">
                        {article.normalized_study_type && (
                          <Badge variant="outline" data-testid="study-type-badge">{article.normalized_study_type}</Badge>
                        )}
                        {article.evidence_level && (
                          <Badge className={getEvidenceLevelColor(article.evidence_level)} data-testid="evidence-level-badge">
                            {article.evidence_level}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-4xl font-bold text-green-600" data-testid="impact-index">
                        {article.impact_index ? article.impact_index.toFixed(1) : 'N/A'}
                      </div>
                      <div className="text-xs text-slate-600">Methodology Score</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MostRigorous;
